/**
 * Декларація з ПДВ J0200126 з додатками Д1–Д6 — ПОВНИЙ бланк (2026-08-05,
 * еталон податкового розрахунку J05: «щоб бухгалтер не лякався»).
 *
 * Вкладки: «Декларація» (офіційний бланк: шапка з реквізитами, розділи
 * I–III, галочки додатків, підписи) + бланк кожного обраного додатка
 * (Д1 — авто з ПН, Д2–Д6 — ручні). Поля редаговані прямо у бланку
 * (жовтий фон). «перевірити» — арифметика + зв'язки з додатками;
 * «друк» — друкована форма всього пакета. Подання: чернетка у Кабінеті
 * ДПС — підпис КЕП і відправка там (пакетом з додатками).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  vatDeclarations,
  type VatAppendixCode,
  type VatAppendixData,
  type VatDeclCheck,
} from '@/api/vatDeclarations';
import { orgs } from '@/api/catalogs';
import { propCoef } from '@/api/vatDocuments';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import VatAppendixTab from './VatAppendixTab';
import {
  APP_LABEL,
  DeclarationHeader,
  DeclarationSections,
  KNOWN_CODES,
  MONTHS_UK,
  SignaturesBlock,
} from './VatDeclarationBlank';
import {
  CorrectionFineBlock,
  CorrectionHeader,
  CorrectionSections,
  CorrectionT2,
} from './VatCorrectionBlank';

const APPENDIX_ORDER: VatAppendixCode[] = ['D1', 'D2', 'D3', 'D4', 'D5', 'D6'];

export default function VatDeclarationEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();

  const [values, setValues] = useState<Record<string, number>>({});
  const [appData, setAppData] = useState<Record<string, VatAppendixData>>({});
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'MAIN' | VatAppendixCode>('MAIN');
  const [check, setCheck] = useState<VatDeclCheck | null>(null);
  const [checking, setChecking] = useState(false);

  const docQ = useQuery({
    queryKey: ['vat-declaration', id],
    queryFn: () => vatDeclarations.get(id!),
    enabled: !!id,
  });
  const d = docQ.data;
  const specsQ = useQuery({
    queryKey: ['vat-appendix-specs'],
    queryFn: vatDeclarations.appendixSpecs,
    staleTime: 3_600_000,
  });
  const subQ = useQuery({
    queryKey: ['vat-declaration-sub', id],
    queryFn: () => vatDeclarations.submission(id!),
    enabled: !!id,
    refetchInterval: 30_000,
  });
  const orgListQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgListQ.data?.items.find((o) => o.id === d?.organization_id);
  // Пропорційний ПДВ (ст.199): діючий коефіцієнт року декларації.
  const propCoefQ = useQuery({
    queryKey: ['prop-coef-for-decl', d?.organization_id, d?.period_year],
    queryFn: () => propCoef.list({
      organization_id: d!.organization_id, year: d!.period_year, limit: 5,
    }),
    enabled: !!d,
  });
  const activeCoef = (propCoefQ.data?.items ?? []).find((c) => c.posted);

  useEffect(() => {
    if (d) {
      setValues(d.values ?? {});
      setAppData(d.appendix_data ?? {});
      setDirty(false);
    }
  }, [d]);

  const subStatus = subQ.data?.status ?? null;
  const submitted = !!subStatus && !['DRAFT', 'ERROR'].includes(subStatus);
  const readOnly = submitted;
  const selectedApps = (d?.appendices ?? []) as VatAppendixCode[];

  const save = useMutation({
    mutationFn: () => vatDeclarations.update(id!, { values, appendix_data: appData }),
    onSuccess: () => {
      setDirty(false); setError(null); setCheck(null);
      qc.invalidateQueries({ queryKey: ['vat-declaration', id] });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? String(e)),
  });
  const recalc = useMutation({
    mutationFn: () => vatDeclarations.recalc(id!),
    onSuccess: () => {
      setError(null); setCheck(null);
      qc.invalidateQueries({ queryKey: ['vat-declaration', id] });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? String(e)),
  });
  const remove = useMutation({
    mutationFn: () => vatDeclarations.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vat-declarations'] });
      navigate('/taxation/vat-declarations');
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? String(e)),
  });
  const setAppendices = useMutation({
    mutationFn: (appendices: VatAppendixCode[]) =>
      vatDeclarations.update(id!, { appendices }),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['vat-declaration', id] });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? String(e)),
  });
  const saveDocDate = useMutation({
    // Дата подання (D_FILL/HFILL основного і додатків).
    mutationFn: (ymd: string) =>
      vatDeclarations.update(id!, { document_date: `${ymd}T00:00:00Z` }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vat-declaration', id] }),
    onError: (e: any) => setError(e?.response?.data?.detail ?? String(e)),
  });

  async function runCheck() {
    if (!id) return;
    setChecking(true);
    try {
      if (dirty && !readOnly) await save.mutateAsync();
      setCheck(await vatDeclarations.check(id));
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? String(e));
    } finally {
      setChecking(false);
    }
  }

  async function openPrint() {
    if (dirty && !readOnly) await save.mutateAsync();
    navigate(`/taxation/vat-declarations/${id}/print`);
  }

  if (!d) return <div className="card">Завантаження…</div>;

  const isCorrection = d.report_type === 'CORRECTING';
  // Уточнюючі додатки — лише Д1–Д5 (Д6-скарга з УР не подається).
  const appendixChoices: VatAppendixCode[] = isCorrection
    ? ['D1', 'D2', 'D3', 'D4', 'D5'] : APPENDIX_ORDER;

  const setV = (code: string, raw: string) => {
    const n = parseInt(raw, 10);
    setValues((v) => {
      const next = { ...v };
      if (!raw || !n) delete next[code];
      else next[code] = n;
      return next;
    });
    setDirty(true);
  };

  const appHasData = (code: VatAppendixCode): boolean => {
    const a = appData[code];
    if (!a) return false;
    if (Object.values(a.fields ?? {}).some((v) => v !== null && v !== '' && v !== 0)) return true;
    return Object.entries(a).some(([k, v]) =>
      k !== 'fields' && Array.isArray(v) && v.length > 0);
  };
  const includedApps = APPENDIX_ORDER.filter(
    (c) => selectedApps.includes(c) && appHasData(c));

  // Інші (рідкісні) заповнені коди поза бланком — щоб нічого не загубити.
  const otherCodes = Object.keys(values).filter((c) => !KNOWN_CODES.has(c)).sort();

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>
          {isCorrection
            ? `Уточнюючий розрахунок з ПДВ — за ${d.corrected_period_month
                ? MONTHS_UK[d.corrected_period_month - 1] : ''} ${d.corrected_period_year ?? ''}`
            : `Декларація з ПДВ — ${MONTHS_UK[d.period_month - 1]} ${d.period_year}`}
        </h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/taxation/vat-declarations')}>до списку</button>
          {!readOnly && (
            <>
              <button className="primary" onClick={() => save.mutate()}
                disabled={save.isPending || !dirty}>
                зберегти
              </button>
              <button onClick={runCheck} disabled={checking}>
                {checking ? '…' : 'перевірити'}
              </button>
              <button onClick={() => {
                confirm(isCorrection
                  ? 'Пересіяти гр.4/гр.5 з останньої декларації уточнюваного періоду? Ручні правки буде перезаписано.'
                  : 'Перерахувати з обліку? Ручні правки рядків декларації і Д1 буде перезаписано (Д2–Д6 не чіпаються).')
                  .then((ok) => { if (ok) recalc.mutate(); });
              }} disabled={recalc.isPending}>
                перерахувати з обліку
              </button>
              <button className="danger" onClick={() => {
                confirm('Видалити декларацію?').then((ok) => { if (ok) remove.mutate(); });
              }}>видалити</button>
            </>
          )}
          <button onClick={openPrint}
            title="Друкована форма декларації з усіма заповненими додатками">
            друк
          </button>
        </div>
      </div>

      {activeCoef && (
        <div style={{ background: 'var(--primary-soft)', border: '1px solid var(--badge-blue-bd)', borderRadius: 6,
          padding: '0.4rem 0.75rem', fontSize: '0.85rem', color: 'var(--badge-blue-fg)',
          margin: '0.5rem 0' }}>
          Пропорційний ПДВ (ст. 199): діючий коефіцієнт (ЧВ) на {d.period_year} рік —{' '}
          <b>{(Number(activeCoef.coefficient) * 100).toFixed(2)}%</b>
          {' '}(док. №{activeCoef.document_number}, на основі {activeCoef.based_on_year} року).
          Не забудьте щомісячну зведену ПН за ст. 199.1 та річний перерахунок у грудні.
        </div>
      )}

      {/* Статус у Кабінеті ДПС — єдиний рядок-банер (еталон J05). */}
      <div style={{ background: 'var(--primary-soft)', border: '1px solid var(--badge-blue-bd)',
        color: 'var(--badge-blue-fg)', padding: '0.5rem 0.75rem', borderRadius: 6,
        marginBottom: '0.75rem', display: 'flex', gap: '0.5rem',
        alignItems: 'center', flexWrap: 'wrap' }}>
        <span>
          {subStatus === 'ACCEPTED' ? '✓ прийнято ДПС (квитанція №2)'
            : subStatus === 'REJECTED' ? '✗ відхилено ДПС'
            : subStatus === 'DELIVERED' ? '✓ доставлено (кв.1), очікуємо кв.2'
            : subStatus === 'SENT' ? '→ відправлено у ДПС'
            : subStatus === 'SIGNED' ? 'підписано — лишилося відправити у Кабінеті ДПС'
            : 'збережено чернеткою у Кабінеті ДПС — підпишіть і відправте там (пакетом з додатками)'}
        </span>
        <a style={{ cursor: 'pointer', marginLeft: 'auto' }}
          onClick={async () => {
            if (dirty && !readOnly) await vatDeclarations.update(id!, { values, appendix_data: appData });
            navigate('/dps-cabinet');
          }}>Кабінет ДПС →</a>
      </div>

      <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: 8,
        display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <span>
          {d.document_number} · суми у гривнях без копійок · авторозрахунок з
          регістрів ПДВ, кожне поле бланка можна виправити вручну.
        </span>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}
          title="Дата подання (D_FILL/HFILL) в декларації і всіх додатках">
          Дата подання
          <div style={{ width: 130 }}>
            <DatePopover value={d.document_date?.slice(0, 10) ?? ''}
              onChange={(v) => { if (v && !readOnly) saveDocDate.mutate(v); }} />
          </div>
        </label>
      </div>
      {error && (
        <div style={{ color: '#dc2626', marginBottom: 8, whiteSpace: 'pre-wrap' }}>{error}</div>
      )}

      {/* Результати перевірки (еталон J05). */}
      {check && (
        <div style={{ marginBottom: '0.75rem' }}>
          {check.errors.length === 0 && check.warnings.length === 0 && (
            <div style={{ background: 'var(--badge-green-bg)', border: '1px solid #bbf7d0',
              color: 'var(--badge-green-fg)', padding: '0.5rem 0.75rem', borderRadius: 6 }}>
              ✓ Помилок не знайдено — декларація готова до подання
              {check.included_appendices.length
                ? ` (пакет: декларація + ${check.included_appendices.map((c) => APP_LABEL[c as VatAppendixCode] ?? c).join(', ')})`
                : ' (без додатків)'}
            </div>
          )}
          {check.errors.length > 0 && (
            <div style={{ background: 'var(--badge-red-bg)', border: '1px solid #fecaca',
              color: 'var(--badge-red-fg)', padding: '0.5rem 0.75rem', borderRadius: 6,
              marginBottom: '0.5rem' }}>
              <b>Помилки ({check.errors.length}) — виправте перед поданням:</b>
              <ul style={{ margin: '0.3rem 0 0 1.1rem', padding: 0 }}>
                {check.errors.map((e, i) => (
                  <li key={i} style={{ fontSize: '0.85rem' }}>
                    [{e.form === 'MAIN' ? 'Декларація' : e.form}] {e.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {check.warnings.length > 0 && (
            <div style={{ background: 'var(--badge-amber-bg)', border: '1px solid #fde68a',
              color: 'var(--badge-amber-fg)', padding: '0.5rem 0.75rem', borderRadius: 6 }}>
              <b>Попередження ({check.warnings.length}) — перевірте:</b>
              <ul style={{ margin: '0.3rem 0 0 1.1rem', padding: 0 }}>
                {check.warnings.map((w, i) => (
                  <li key={i} style={{ fontSize: '0.85rem' }}>
                    [{w.form === 'MAIN' ? 'Декларація' : w.form}] {w.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Вкладки: головна форма + обрані додатки; поруч — редактор складу. */}
      <div className="seg-tabs" style={{ marginBottom: '0.6rem', flexWrap: 'wrap' }}>
        <button className={tab === 'MAIN' ? 'active' : ''}
          onClick={() => setTab('MAIN')}>
          {isCorrection ? 'Розрахунок' : 'Декларація'}
        </button>
        {appendixChoices.filter((c) => selectedApps.includes(c)).map((c) => (
          <button key={c} className={tab === c ? 'active' : ''}
            onClick={() => setTab(c)}
            title={specsQ.data?.[c]?.title}>
            {APP_LABEL[c]}{appHasData(c) ? '' : ' (порожній)'}
          </button>
        ))}
        {!readOnly && (
          <select value="" style={{ width: 190, marginLeft: 'auto' }}
            title="Додати або прибрати додаток (порожній додаток у пакет не вкладається)"
            onChange={(e) => {
              const code = e.target.value as VatAppendixCode;
              if (!code) return;
              const next = selectedApps.includes(code)
                ? selectedApps.filter((x) => x !== code)
                : [...selectedApps, code];
              if (selectedApps.includes(code) && appHasData(code)) {
                confirm(`Прибрати ${APP_LABEL[code]}? Введені дані бланка буде втрачено при поданні.`)
                  .then((ok) => { if (ok) { setAppendices.mutate(next); if (tab === code) setTab('MAIN'); } });
              } else {
                setAppendices.mutate(next);
                if (selectedApps.includes(code) && tab === code) setTab('MAIN');
              }
            }}>
            <option value="">склад додатків…</option>
            {appendixChoices.map((c) => (
              <option key={c} value={c}>
                {selectedApps.includes(c) ? `✓ ${APP_LABEL[c]} — прибрати` : `+ ${APP_LABEL[c]} — додати`}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* УР — бланк «було/стало/різниця» на вкладці «Розрахунок». */}
      {isCorrection && tab === 'MAIN' && (
        <div>
          <CorrectionHeader doc={d} org={org} />
          <CorrectionSections values={values} readOnly={readOnly}
            onEdit={(patch) => {
              setValues((v) => {
                const next = { ...v };
                for (const [code, val] of Object.entries(patch)) {
                  if (val == null) delete next[code];
                  else next[code] = val;
                }
                return next;
              });
              setDirty(true);
            }} />
          <CorrectionFineBlock values={values} setV={setV} readOnly={readOnly} />
          <CorrectionT2
            rows={((appData['UR_T2'] as any)?.rows as any[]) ?? []}
            readOnly={readOnly}
            onChange={(rows) => {
              setAppData((s) => ({ ...s, UR_T2: { rows } as any }));
              setDirty(true);
            }} />
          <SignaturesBlock doc={d} org={org} />
          <p style={{ color: 'var(--muted)', fontSize: '0.82rem',
            marginTop: '0.5rem' }}>
            Гр.4 «було» засіяно з останньої декларації уточнюваного періоду
            (якщо вона подавалась поза системою — внесіть гр.4 вручну).
            Гр.6 рахується автоматично. Уточнюючі додатки Д1–Д5 — вкладками
            вище (позначаються «Уточнююча», подаються пакетом з УР).
          </p>
        </div>
      )}

      {/* Бланк — офіційний вигляд (еталон J05): рамки, зарубки, шапка. */}
      {!isCorrection && tab === 'MAIN' && (
        <div>
          <DeclarationHeader doc={d} org={org} includedApps={includedApps} />
          <DeclarationSections values={values} setV={setV} readOnly={readOnly} />
          {otherCodes.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse',
              marginTop: 6 }}>
              <tbody>
                <tr>
                  <td style={{ border: '1px solid #333', padding: '2px 6px',
                    fontWeight: 700, fontSize: '0.8rem' }} colSpan={2}>
                    Інші заповнені поля форми (коди XSD)
                  </td>
                </tr>
                {otherCodes.map((c) => (
                  <tr key={c}>
                    <td style={{ border: '1px solid #333', padding: '2px 6px',
                      fontFamily: 'monospace', fontSize: '0.8rem' }}>{c}</td>
                    <td style={{ border: '1px solid #333', padding: '1px 3px',
                      width: 130 }}>
                      <input type="number" step="1" value={values[c] ?? ''}
                        disabled={readOnly}
                        style={{ width: '100%', textAlign: 'right',
                          border: 'none', outline: 'none',
                          background: 'var(--badge-amber-bg)' }}
                        onChange={(e) => setV(c, e.target.value)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <SignaturesBlock doc={d} org={org} />
        </div>
      )}

      {tab !== 'MAIN' && specsQ.data?.[tab] && (
        <div>
          <VatAppendixTab
            spec={specsQ.data[tab]}
            appLabel={APP_LABEL[tab]}
            data={appData[tab] ?? { fields: {} }}
            doc={d}
            org={org}
            readOnly={readOnly}
            onChange={(next) => {
              setAppData((s) => ({ ...s, [tab]: next }));
              setDirty(true);
            }}
          />
        </div>
      )}
    </div>
  );
}
